import TurndownService from "turndown";

export const MINI_WIDGET_SOURCE = "mm-ai-mini";
export const MINI_WIDGET_VERSION = 1;
export const MINI_MARKDOWN_MAX_LENGTH = 30_000;
export const MINI_AUTO_OPEN_SUPPRESSION_MS = 60 * 60 * 1_000;
export const MINI_LAUNCHER_ICON_URL =
	"https://cdn.macromicro.me/assets/img/favicons/mm-ai.svg";

const suppressionStorageKey = "mm-ai-mini:auto-open-suppressed-until";
const pageTypes = new Set([
	"article",
	"chart",
	"collection",
	"quickie",
	"video",
	"trader-insight",
	"toolbox",
	"other",
]);
const languageByHostname = new Map([
	["www.macromicro.me", "tc"],
	["sc.macromicro.me", "sc"],
	["en.macromicro.me", "en"],
	["dev.macromicro.me", "tc"],
	["dev-sc.macromicro.me", "sc"],
	["dev-en.macromicro.me", "en"],
	["debug.macromicro.me", "tc"],
	["debug-sc.macromicro.me", "sc"],
	["debug-en.macromicro.me", "en"],
]);
const copy = {
	tc: {
		anonymousBody:
			"加入 MM 會員，體驗最強 MM AI 研究員協助你分析總經趨勢，以及讓 AI 客服解決網站使用問題。",
		anonymousClose: "關閉 MM AI",
		anonymousTitle: "Hi，我是 MM AI",
		ask: "AI Chat Mini",
		body: "我會根據研究員的分析方式，快速串聯 M 平方的圖表與研究內容，幫助你判讀趨勢、做出決策！",
		close: "關閉",
		iframeTitle: "MacroMicro AI 對話",
		introTitle: "Hi，我是 MM AI 研究員",
		launcher: "開啟 MM AI",
		login: "立即登入",
	},
	sc: {
		anonymousBody:
			"加入 MM 会员，体验最强 MM AI 研究员协助您分析宏观趋势，以及让 AI 客服解决网站使用问题。",
		anonymousClose: "关闭 MM AI",
		anonymousTitle: "Hi，我是 MM AI",
		ask: "AI Chat Mini",
		body: "我会根据研究员的分析方式，快速串联 M 平方的图表与研究内容，帮助你判读趋势、做出决策！",
		close: "关闭",
		iframeTitle: "MacroMicro AI 对话",
		introTitle: "Hi，我是 MM AI 研究员",
		launcher: "开启 MM AI",
		login: "立即登录",
	},
	en: {
		anonymousBody:
			"Join MacroMicro to try the MM AI researcher for macro trend analysis and AI support for site questions.",
		anonymousClose: "Close MM AI",
		anonymousTitle: "Hi, I’m MM AI",
		ask: "AI Chat Mini",
		body: "I connect MacroMicro charts and research to help you make better decisions.",
		close: "Close",
		iframeTitle: "MacroMicro AI chat",
		introTitle: "Hi, I'm MM AI Analyst",
		launcher: "Open MM AI",
		login: "Log in",
	},
};

function isLocalHostname(hostname) {
	return hostname === "localhost" || hostname === "127.0.0.1";
}

function isAiDemoHostname(hostname) {
	return (
		hostname === "ai.macromicro.me" ||
		hostname === "dev-ai.macromicro.me" ||
		hostname === "debug-ai.macromicro.me"
	);
}

function isEstimateTabPage() {
	const pathname = window.location.pathname.replace(/\/+$/, "");
	return (
		/(?:^|\/)stocks\/info\/[^/]+$/i.test(pathname) &&
		window.location.hash === "#Estimate"
	);
}

export function resolveMiniLanguage(hostname, fallbackLanguage) {
	const mapped = languageByHostname.get(hostname);
	if (mapped) return mapped;
	if (
		(isLocalHostname(hostname) || isAiDemoHostname(hostname)) &&
		(fallbackLanguage === "tc" ||
			fallbackLanguage === "sc" ||
			fallbackLanguage === "en")
	) {
		return fallbackLanguage;
	}
	return null;
}

export function isMiniAutoOpenSuppressed(storage, now = Date.now()) {
	try {
		const until = Number(storage.getItem(suppressionStorageKey));
		if (!Number.isFinite(until) || until <= now) {
			storage.removeItem(suppressionStorageKey);
			return false;
		}
		return true;
	} catch {
		return false;
	}
}

function suppressMiniAutoOpen(storage, now = Date.now()) {
	try {
		storage.setItem(
			suppressionStorageKey,
			String(now + MINI_AUTO_OPEN_SUPPRESSION_MS),
		);
	} catch {
		// Storage can be unavailable in privacy modes; closing still succeeds.
	}
}

function removeJunk(root) {
	const selectors = [
		"script",
		"style",
		"noscript",
		"template",
		"svg",
		"canvas",
		"nav",
		"footer",
		"form",
		"input",
		"button",
		"dialog",
		"[hidden]",
		'[aria-hidden="true"]',
		".hidden",
		".d-none",
		'[style*="display:none"]',
		'[style*="display: none"]',
		'[data-duplicate-rendering="true"]',
		".highcharts-container",
		".highcharts-root",
		".highcharts-a11y-proxy-container",
		".highcharts-exporting-group",
		".breadcrumb",
		".breadcrumbs",
		".social-share",
		".share-buttons",
		".advertisement",
		".ads",
		'[data-ad]',
		'[data-tracking-only="true"]',
		"[data-mm-skip]",
	];
	for (const element of root.querySelectorAll(selectors.join(","))) {
		element.remove();
	}
	for (const element of root.querySelectorAll("*")) {
		for (const attribute of Array.from(element.attributes)) {
			if (
				attribute.name === "style" ||
				attribute.name.startsWith("on") ||
				attribute.name.startsWith("data-")
			) {
				element.removeAttribute(attribute.name);
			}
		}
	}
}

function normalizeMarkdown(markdown) {
	return markdown
		.replace(/\u00a0/g, " ")
		.replace(/[ \t]+\n/g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

export function contentRootToMarkdown(contentRoot) {
	if (
		!(contentRoot instanceof Element) ||
		contentRoot.hasAttribute("data-mm-skip")
	) {
		return { markdown: "", truncated: false };
	}
	const clone = contentRoot.cloneNode(true);
	removeJunk(clone);
	const service = new TurndownService({
		bulletListMarker: "-",
		codeBlockStyle: "fenced",
		headingStyle: "atx",
	});
	let markdown = normalizeMarkdown(service.turndown(clone));
	const characterCount = Array.from(markdown).length;
	const truncated = characterCount > MINI_MARKDOWN_MAX_LENGTH;
	if (truncated) {
		markdown = Array.from(markdown)
			.slice(0, MINI_MARKDOWN_MAX_LENGTH)
			.join("")
			.trimEnd();
	}
	return { markdown, truncated };
}

function resolveElement(value) {
	if (value instanceof Element) return value;
	if (typeof value === "string") return document.querySelector(value);
	return document.querySelector("main");
}

function normalizePageType(value) {
	return pageTypes.has(value) ? value : "other";
}

function normalizeUrl(value, fallback) {
	try {
		const url = new URL(value || fallback, window.location.href);
		if (url.href.length <= 2_048) return url.href;
		url.search = "";
		url.hash = "";
		url.pathname = url.pathname.slice(0, 1_500);
		return url.href.slice(0, 2_048);
	} catch {
		return fallback;
	}
}

function optionalBoundedText(value, maxLength) {
	const text = value == null ? "" : String(value).trim();
	return text ? text.slice(0, maxLength) : null;
}

export function stripTrailingSiteNameFromTitle(title) {
	const text = title == null ? "" : String(title).trim();
	const separatorIndex = text.lastIndexOf("|");
	if (separatorIndex <= 0) return text;
	const withoutSiteName = text.slice(0, separatorIndex).trim();
	return withoutSiteName || text;
}

export function createMiniPageContext(options = {}) {
	const page = options.page ?? {};
	const root = resolveElement(options.contentRoot);
	const { markdown, truncated } = contentRootToMarkdown(root);
	const canonicalFromDocument = document.querySelector(
		'link[rel="canonical"]',
	)?.href;
	const canonicalUrl = page.canonicalUrl || canonicalFromDocument;
	const title = optionalBoundedText(
		stripTrailingSiteNameFromTitle(page.title || document.title),
		500,
	);
	const contentId = optionalBoundedText(page.contentId, 256);
	const slug = optionalBoundedText(page.slug, 512);
	return {
		version: 1,
		pageType: normalizePageType(page.pageType),
		url: normalizeUrl(window.location.href, window.location.origin),
		...(canonicalUrl
			? { canonicalUrl: normalizeUrl(canonicalUrl, window.location.href) }
			: {}),
		...(title ? { title } : {}),
		...(contentId ? { contentId } : {}),
		...(slug ? { slug } : {}),
		content: {
			format: "markdown",
			markdown,
			truncated,
		},
	};
}

export function createLiveMiniPageContext(options) {
	return createMiniPageContext({
		...options,
		page: {
			...(options.page ?? {}),
			title: document.title,
		},
	});
}

export function createMiniFrameUrl(
	widgetOrigin,
	instanceId,
	parentOrigin,
) {
	const frameQuery = new URLSearchParams({
		instanceId,
		parentOrigin,
	});
	return `${widgetOrigin}/mini?${frameQuery.toString()}`;
}

export function createHostLoginUrl(hostUrl) {
	try {
		const loginUrl = new URL("/login", hostUrl);
		loginUrl.searchParams.set("next", new URL(hostUrl).href);
		return loginUrl.href;
	} catch {
		return "/login";
	}
}

function resolveWidgetOrigin(scriptUrl, hostname) {
	if (isLocalHostname(hostname)) return window.location.origin;
	if (hostname.startsWith("dev")) return "https://dev-ai.macromicro.me";
	if (hostname.startsWith("debug")) return "https://debug-ai.macromicro.me";
	if (languageByHostname.has(hostname)) return "https://ai.macromicro.me";
	try {
		const origin = new URL(scriptUrl).origin;
		if (
			origin === "https://ai.macromicro.me" ||
			origin === "https://dev-ai.macromicro.me" ||
			origin === "https://debug-ai.macromicro.me"
		) {
			return origin;
		}
	} catch {
		// Fall through to an unsupported host error.
	}
	return null;
}

function createEnvelope(instanceId, action, payload) {
	return {
		source: MINI_WIDGET_SOURCE,
		version: MINI_WIDGET_VERSION,
		action,
		instanceId,
		payload,
	};
}

export function createStyles() {
	return `
		:host { all: initial; }
		*, *::before, *::after { box-sizing: border-box; }
		.mm-mini-root { position: fixed; z-index: 2147483000; right: 20px; bottom: 20px; display: flex; flex-direction: column; align-items: flex-end; font-family: "Geist Variable", Inter, system-ui, sans-serif; color: #172033; }
		.mm-mini-launcher { width: 70px; height: 70px; border: 3px solid white; border-radius: 999px; padding: 0; cursor: pointer; color: white; background: transparent; box-shadow: 0 14px 40px rgba(14,30,58,.28); display: grid; place-items: center; overflow: hidden; transition: transform .18s ease; }
		.mm-mini-launcher:hover { transform: translateY(-2px); }
		.mm-mini-launcher img { display: block; width: 64px; height: 64px; border-radius: inherit; object-fit: cover; }
		.mm-mini-welcome { position: absolute; right: 0; bottom: 78px; width: min(420px, calc(100vw - 32px)); padding: 3px; border-radius: 18px; background: linear-gradient(135deg,#4964d0,#50e3c2); box-shadow: 0 18px 52px rgba(14,30,58,.24); }
		.mm-mini-welcome-inner { border-radius: 15px; background: #fff; padding: 22px; }
		.mm-mini-welcome h2 { margin: 0 0 9px; font-size: 22px; line-height: 1.2; }
		.mm-mini-welcome p { margin: 0; color: #586274; font-size: 15px; line-height: 1.65; }
		.mm-mini-welcome-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
		.mm-mini-welcome button, .mm-mini-welcome a { border: 0; border-radius: 999px; cursor: pointer; padding: 10px 18px; font: inherit; font-weight: 700; line-height: normal; text-decoration: none; }
		.mm-mini-welcome-open { color: white; background: #4663ce; }
		.mm-mini-welcome-close { color: #586274; background: #eef1f5; }
		.mm-mini-frame-wrap { position: absolute; right: 0; bottom: 76px; width: clamp(440px,38vw,560px); height: min(720px,calc(100dvh - 120px)); padding: 3px; border-radius: 24px; background: linear-gradient(135deg,#4964d0,#50e3c2); box-shadow: 0 20px 60px rgba(14,30,58,.3); contain: layout paint style; isolation: isolate; overflow: hidden; opacity: 0; pointer-events: none; transform: translate3d(0,4px,0); visibility: hidden; will-change: opacity, transform; -webkit-transform: translate3d(0,4px,0); }
		.mm-mini-root[data-open="true"] .mm-mini-frame-wrap { opacity: 1; pointer-events: auto; transform: translate3d(0,0,0); visibility: visible; -webkit-transform: translate3d(0,0,0); }
		.mm-mini-frame { display: block; width: 100%; height: 100%; border: 0; border-radius: 21px; background: #fff; color-scheme: light; transform: translate3d(0,0,0); -webkit-transform: translate3d(0,0,0); }
		[hidden] { display: none !important; }
		@media (max-width: 599px) {
			.mm-mini-root { inset: auto 8px max(8px,env(safe-area-inset-bottom)) 8px; }
			.mm-mini-root[data-open="true"] { top: max(8px,env(safe-area-inset-top)); }
			.mm-mini-root[data-open="true"] .mm-mini-launcher { display: none; }
			.mm-mini-frame-wrap { border-radius: 24px; bottom: 76px; height: auto; left: 0; right: 0; width: auto; }
			.mm-mini-root[data-open="true"] .mm-mini-frame-wrap { inset: 0; }
			.mm-mini-frame { border-radius: 21px; }
			.mm-mini-welcome { position: fixed; right: 8px; bottom: 86px; left: 8px; width: auto; }
		}
		@media (prefers-reduced-motion: reduce) { .mm-mini-launcher { transition: none; } }
	`;
}

function launcherIcon() {
	return `<img src="${MINI_LAUNCHER_ICON_URL}" alt="" aria-hidden="true" />`;
}

export function createInstance(options, scriptUrl) {
	const language = resolveMiniLanguage(
		window.location.hostname,
		options.devLanguage,
	);
	const widgetOrigin = resolveWidgetOrigin(
		scriptUrl,
		window.location.hostname,
	);
	if (!language || !widgetOrigin) {
		throw new Error("MM AI mini widget is not configured for this host.");
	}

	const instanceId = crypto.randomUUID();
	const host = document.createElement("div");
	host.id = "mm-ai-mini-widget";
	const shadow = host.attachShadow({ mode: "open" });
	const style = document.createElement("style");
	style.textContent = createStyles();
	const root = document.createElement("div");
	root.className = "mm-mini-root";
	root.dataset.open = "false";
	const frameWrap = document.createElement("div");
	frameWrap.className = "mm-mini-frame-wrap";
	const launcher = document.createElement("button");
	launcher.className = "mm-mini-launcher";
	launcher.type = "button";
	launcher.setAttribute("aria-label", copy[language].launcher);
	launcher.innerHTML = launcherIcon();
	const welcome = document.createElement("section");
	welcome.className = "mm-mini-welcome";
	welcome.hidden = true;
	welcome.setAttribute("aria-label", copy[language].introTitle);
	welcome.innerHTML = `<div class="mm-mini-welcome-inner"><h2>${copy[language].introTitle}</h2><p>${copy[language].body}</p><div class="mm-mini-welcome-actions"><button class="mm-mini-welcome-close" type="button">${copy[language].close}</button><button class="mm-mini-welcome-open" type="button">${copy[language].ask}</button></div></div>`;
	const loginPrompt = document.createElement("section");
	loginPrompt.className = "mm-mini-welcome mm-mini-login";
	loginPrompt.hidden = true;
	loginPrompt.setAttribute("aria-label", copy[language].anonymousTitle);
	loginPrompt.innerHTML = `<div class="mm-mini-welcome-inner"><h2>${copy[language].anonymousTitle}</h2><p>${copy[language].anonymousBody}</p><div class="mm-mini-welcome-actions"><button class="mm-mini-welcome-close" type="button">${copy[language].anonymousClose}</button><a class="mm-mini-welcome-open" href="${createHostLoginUrl(window.location.href)}">${copy[language].login}</a></div></div>`;
	root.append(frameWrap, welcome, loginPrompt, launcher);
	shadow.append(style, root);
	document.body.append(host);

	let iframe = null;
	let isOpen = false;
	const initialAuthenticated =
		typeof options.isAuthenticated === "boolean"
			? options.isAuthenticated
			: options.authenticatedHint === true;
	let authRequired = !initialAuthenticated;
	let pageContext = createMiniPageContext(options);
	let hostUrl = window.location.href;
	let destroyed = false;
	let navigationFrameId = null;
	let navigationRefreshVersion = 0;
	const repaintTimerIds = new Set();

	function postToFrame(action, payload) {
		iframe?.contentWindow?.postMessage(
			createEnvelope(instanceId, action, payload),
			widgetOrigin,
		);
	}

	function configureFrame() {
		postToFrame("configure", {
			language,
			pageContext,
			authenticatedHint: !authRequired,
			hostUrl,
		});
		scheduleFrameRepaint();
	}

	function forceFrameRepaint() {
		if (!iframe || destroyed || !isOpen) return;
		const previousOpacity = iframe.style.opacity;
		iframe.style.opacity = "0.999";
		void iframe.offsetHeight;
		window.requestAnimationFrame(() => {
			if (!iframe || destroyed || !isOpen) return;
			iframe.style.opacity = previousOpacity;
			void iframe.offsetHeight;
		});
	}

	function scheduleFrameRepaint() {
		if (!isOpen) return;
		forceFrameRepaint();
		for (const delay of [50, 150, 350, 700]) {
			const timerId = window.setTimeout(() => {
				repaintTimerIds.delete(timerId);
				forceFrameRepaint();
			}, delay);
			repaintTimerIds.add(timerId);
		}
	}

	function clearScheduledRepaints() {
		for (const timerId of repaintTimerIds) {
			window.clearTimeout(timerId);
		}
		repaintTimerIds.clear();
	}

	function clearScheduledNavigationRefresh() {
		navigationRefreshVersion += 1;
		if (navigationFrameId !== null) {
			window.cancelAnimationFrame(navigationFrameId);
			navigationFrameId = null;
		}
	}

	function ensureFrame() {
		if (iframe) return iframe;
		iframe = document.createElement("iframe");
		iframe.className = "mm-mini-frame";
		iframe.title = copy[language].iframeTitle;
		iframe.allow = "clipboard-write";
		iframe.src = createMiniFrameUrl(
			widgetOrigin,
			instanceId,
			window.location.origin,
		);
		iframe.addEventListener("load", scheduleFrameRepaint);
		frameWrap.append(iframe);
		return iframe;
	}

	function open() {
		if (destroyed) return;
		if (authRequired) {
			showLoginPrompt();
			return;
		}
		ensureFrame();
		isOpen = true;
		root.dataset.open = "true";
		welcome.hidden = true;
		loginPrompt.hidden = true;
		scheduleFrameRepaint();
	}

	function close({ suppressAutoOpen = true } = {}) {
		if (!isOpen && welcome.hidden && loginPrompt.hidden) return;
		isOpen = false;
		root.dataset.open = "false";
		welcome.hidden = true;
		loginPrompt.hidden = true;
		clearScheduledRepaints();
		if (suppressAutoOpen) suppressMiniAutoOpen(window.localStorage);
		launcher.focus({ preventScroll: true });
	}

	function syncVisibility() {
		const shouldHide = isEstimateTabPage();
		if (shouldHide) {
			close({ suppressAutoOpen: false });
		}
		host.hidden = shouldHide;
	}

	function showLoginPrompt() {
		isOpen = false;
		root.dataset.open = "false";
		welcome.hidden = true;
		loginPrompt.hidden = false;
		clearScheduledRepaints();
	}

	function setAuthenticated(isAuthenticated) {
		const wasAuthRequired = authRequired;
		authRequired = isAuthenticated !== true;
		if (authRequired) {
			if (isOpen || !welcome.hidden) showLoginPrompt();
			return;
		}
		if (wasAuthRequired && iframe) {
			iframe.remove();
			iframe = null;
		}
		loginPrompt.hidden = true;
	}

	function refreshPageContext({
		cancelScheduledNavigation = false,
		newThread = false,
		requestId = null,
	} = {}) {
		if (cancelScheduledNavigation) {
			clearScheduledNavigationRefresh();
		}
		const nextUrl = window.location.href;
		pageContext = createLiveMiniPageContext(options);
		const action = newThread || nextUrl !== hostUrl
			? "host-page-changed"
			: "page-context";
		hostUrl = nextUrl;
		loginPrompt
			.querySelector(".mm-mini-welcome-open")
			.setAttribute("href", createHostLoginUrl(hostUrl));
		postToFrame(action, {
			hostUrl,
			pageContext,
			...(typeof requestId === "string" ? { requestId } : {}),
		});
	}

	function scheduleNavigationRefresh() {
		clearScheduledNavigationRefresh();
		const refreshVersion = navigationRefreshVersion;
		queueMicrotask(() => {
			if (destroyed || refreshVersion !== navigationRefreshVersion) return;
			navigationFrameId = window.requestAnimationFrame(() => {
				if (destroyed || refreshVersion !== navigationRefreshVersion) return;
				navigationFrameId = window.requestAnimationFrame(() => {
					navigationFrameId = null;
					if (destroyed || refreshVersion !== navigationRefreshVersion) return;
					refreshPageContext({ newThread: true });
				});
			});
		});
	}

	function onMessage(event) {
		if (
			destroyed ||
			event.origin !== widgetOrigin ||
			event.source !== iframe?.contentWindow
		) {
			return;
		}
		const message = event.data;
		if (
			!message ||
			message.source !== MINI_WIDGET_SOURCE ||
			message.version !== MINI_WIDGET_VERSION ||
			message.instanceId !== instanceId ||
			typeof message.action !== "string"
		) {
			return;
		}
		switch (message.action) {
			case "ready":
				configureFrame();
				break;
			case "configured":
				scheduleFrameRepaint();
				break;
			case "close":
				close();
				break;
			case "auth-required":
				authRequired = true;
				showLoginPrompt();
				break;
			case "request-page-context":
				refreshPageContext({
					cancelScheduledNavigation: true,
					requestId:
						typeof message.payload?.requestId === "string"
							? message.payload.requestId
							: null,
				});
				break;
			case "open-full-ai": {
				const url = message.payload?.url;
				try {
					const parsed = new URL(url);
					if (parsed.origin === widgetOrigin) {
						window.open(parsed.href, "_blank", "noopener,noreferrer");
					}
				} catch {
					// Ignore malformed handoff URLs.
				}
				break;
			}
		}
	}

	const originalPushState = history.pushState.bind(history);
	const originalReplaceState = history.replaceState.bind(history);
	const onNavigation = () => {
		syncVisibility();
		scheduleNavigationRefresh();
	};
	history.pushState = (...args) => {
		originalPushState(...args);
		onNavigation();
	};
	history.replaceState = (...args) => {
		originalReplaceState(...args);
		onNavigation();
	};
	window.addEventListener("popstate", onNavigation);
	window.addEventListener("hashchange", onNavigation);
	window.addEventListener("message", onMessage);
	window.addEventListener("keydown", (event) => {
		if (
			event.key === "Escape" &&
			(isOpen || !welcome.hidden || !loginPrompt.hidden)
		) {
			close();
		}
	});
	launcher.addEventListener("click", () =>
		isOpen || !welcome.hidden || !loginPrompt.hidden ? close() : open(),
	);
	welcome
		.querySelector(".mm-mini-welcome-open")
		.addEventListener("click", open);
	welcome
		.querySelector(".mm-mini-welcome-close")
		.addEventListener("click", () => close());
	loginPrompt
		.querySelector(".mm-mini-welcome-close")
		.addEventListener("click", () => close());

	if (
		initialAuthenticated &&
		!isMiniAutoOpenSuppressed(window.localStorage)
	) {
		welcome.hidden = false;
	}
	syncVisibility();

	return {
		close,
		destroy() {
			if (destroyed) return;
			destroyed = true;
			clearScheduledNavigationRefresh();
			clearScheduledRepaints();
			window.removeEventListener("message", onMessage);
			window.removeEventListener("popstate", onNavigation);
			window.removeEventListener("hashchange", onNavigation);
			history.pushState = originalPushState;
			history.replaceState = originalReplaceState;
			host.remove();
		},
		open,
		refreshPageContext,
		setAuthenticated,
	};
}

const scriptUrl =
	typeof document === "undefined" ? "" : document.currentScript?.src ?? "";
let activeInstance = null;

export const MacroMicroMiniWidget = {
	init(options = {}) {
		if (activeInstance) return activeInstance;
		activeInstance = createInstance(options, scriptUrl);
		return activeInstance;
	},
	open() {
		activeInstance?.open();
	},
	close() {
		activeInstance?.close();
	},
	refreshPageContext(options) {
		activeInstance?.refreshPageContext(options);
	},
	setAuthenticated(isAuthenticated) {
		activeInstance?.setAuthenticated(isAuthenticated);
	},
	destroy() {
		activeInstance?.destroy();
		activeInstance = null;
	},
};

if (typeof window !== "undefined") {
	window.MacroMicroMiniWidget = MacroMicroMiniWidget;
}
